import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, StatusIcon } from "@/components/shared";
import { FileText } from "lucide-react";
import { toast } from "sonner";

const PAGE_TYPES = ["home", "service", "location", "industry", "article", "resource", "comparison", "caseStudy", "pricing", "product", "tool", "contact", "legal", "other"];

export default function PagesInventory() {
  const { activeWebsite } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [ptype, setPtype] = useState("all");
  const [page, setPage] = useState(1);

  const load = async () => {
    if (!activeWebsite) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (search) params.search = search;
      if (ptype !== "all") params.page_type = ptype;
      const { data: d } = await api.get(`/websites/${activeWebsite.id}/pages`, { params });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [activeWebsite?.id, ptype, page]); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Pages</h1>
          <p className="text-xs text-muted-foreground">Crawled page inventory with SEO elements, links and indexability.</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Search URL…" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} className="h-8 w-48 text-xs" data-testid="pages-search-input" />
          <Select value={ptype} onValueChange={(v) => { setPtype(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="pages-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {PAGE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <Skeleton className="h-64 w-full" /> : !data || data.total === 0 ? (
        <EmptyState icon={FileText} title="No pages crawled yet" message="Run a website audit from the Overview page to build the inventory." testId="pages-empty" />
      ) : (
        <Card className="shadow-none">
          <CardContent className="scc-table-wrap p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>URL</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="hidden md:table-cell">Title</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                  <TableHead className="text-right">Words</TableHead>
                  <TableHead className="text-right">In / Out links</TableHead>
                  <TableHead className="text-right">Depth</TableHead>
                  <TableHead>Indexable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((p) => (
                  <TableRow key={p.id} data-testid="pages-table-row">
                    <TableCell className="max-w-[260px] truncate font-mono text-xs">{p.url}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{p.page_type}</Badge></TableCell>
                    <TableCell className="hidden max-w-[240px] truncate text-xs md:table-cell">{p.title || <span className="text-rose-600">missing</span>}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.status_code}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.word_count}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.incoming_links} / {p.outgoing_links}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.depth}</TableCell>
                    <TableCell><StatusIcon ok={p.indexable} warn={!p.indexable && p.status_code === 200} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {data && data.total > 25 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{data.total} pages</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page * 25 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
